import { useState, useEffect, useCallback, useRef } from "react";
import { TabPosData, TabStackingConfig } from "./types";

export const DEFAULT_CONFIG: TabStackingConfig = {
  maxTabWidth: 200,
  minTabWidth: 200,
  tabHeight: 52,
  overlap: 35,
  stripPadLeft: 0, // 0px gap between unstacked tabs and left tab list icon
  stripPadRight: 16, // 16px right margin for right stacked cards
  maxStackDepth: 4,
  rightStackStep: 8,
  leftStackStep: 8,
};

export function useTabStacking(
  tabsCount: number,
  activeIndex: number,
  userConfig: Partial<TabStackingConfig> = {}
) {
  const config: TabStackingConfig = { ...DEFAULT_CONFIG, ...userConfig };
  
  const [containerWidth, setContainerWidth] = useState(800);
  const [scrollOffset, setScrollOffset] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const prevActiveIndexRef = useRef<number>(activeIndex);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
    if (node !== null) {
      setContainerWidth(node.getBoundingClientRect().width);
    }
  }, []);

  // SSR-safe ResizeObserver
  useEffect(() => {
    const node = containerNodeRef.current;
    if (!node || typeof window === "undefined" || typeof ResizeObserver === "undefined") return;

    const initialWidth = node.getBoundingClientRect().width;
    console.log("Container initial width:", initialWidth);
    setContainerWidth(initialWidth);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        console.log("Container width changed:", entry.contentRect.width);
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Static tab width (180px)
  const tabW = config.maxTabWidth;
  const tabStep = tabW - config.overlap;
  const totalW =
    tabsCount > 0
      ? config.stripPadLeft +
        tabsCount * tabW -
        (tabsCount - 1) * config.overlap +
        config.stripPadRight
      : 0;

  const overflow = totalW > containerWidth;

  // Debug logging
  useEffect(() => {
    if (overflow && scrollRef.current) {
      const scrollEl = scrollRef.current;
      console.log("Tab Scroll Debug:", {
        tabsCount,
        containerWidth,
        totalW,
        overflow,
        scrollWidth: scrollEl.scrollWidth,
        clientWidth: scrollEl.clientWidth,
        scrollLeft: scrollEl.scrollLeft,
        scrollable: scrollEl.scrollWidth > scrollEl.clientWidth,
      });
    }
  }, [tabsCount, containerWidth, totalW, overflow]);

  const rafIdRef = useRef<number | null>(null);

  // Handle scroll events with rAF throttle to eliminate shaking during scroll
  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const left = scrollRef.current.scrollLeft;
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        setScrollOffset(left);
      });
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [handleScroll]);

  // Scroll active tab into view ONLY when activeIndex changes
  const scrollToActiveTab = useCallback(() => {
    const el = scrollRef.current;
    if (!el || tabsCount === 0 || containerWidth <= 0) return;
    
    const targetX = config.stripPadLeft + activeIndex * tabStep;
    const viewport = containerWidth - config.stripPadRight;
    const currentS = el.scrollLeft;

    let targetS = currentS;
    if (targetX < currentS) {
      targetS = targetX - config.stripPadLeft;
    } else if (targetX + tabW > currentS + viewport) {
      targetS = targetX + tabW - viewport;
    }

    if (targetS !== currentS) {
      el.scrollTo({
        left: Math.max(0, targetS),
        behavior: "smooth",
      });
    }
  }, [activeIndex, tabsCount, containerWidth, tabStep, tabW, config]);

  // Only auto-scroll when activeIndex selection actually changes
  useEffect(() => {
    if (prevActiveIndexRef.current !== activeIndex) {
      prevActiveIndexRef.current = activeIndex;
      scrollToActiveTab();
    }
  }, [activeIndex, scrollToActiveTab]);

  // Left stacked padding: 12px so left-stacked tabs sit flush right against tab list icon
  const leftStackedPad = 12;

  // Viewport edges in scroll coordinates
  const leftEdge = scrollOffset;
  const rightEdge = scrollOffset + containerWidth - config.stripPadRight;

  // Identify left-stacked and right-stacked tabs
  const leftStackedIndices: number[] = [];
  const rightStackedIndices: number[] = [];

  for (let i = 0; i < tabsCount; i++) {
    const naturalX = config.stripPadLeft + i * tabStep;
    const naturalRight = naturalX + tabW;

    if (naturalRight > rightEdge) {
      rightStackedIndices.push(i);
    } else if (naturalX < leftEdge + leftStackedPad && leftEdge > 5) {
      leftStackedIndices.push(i);
    }
  }

  // Compute exact positions for every tab
  const positions: TabPosData[] = new Array(tabsCount);

  for (let i = 0; i < tabsCount; i++) {
    const naturalX = config.stripPadLeft + i * tabStep;
    const naturalRight = naturalX + tabW;

    if (naturalRight > rightEdge) {
      const rIdx = rightStackedIndices.indexOf(i);
      const stackIndex = rIdx >= 0 ? rIdx : 0;
      const isHidden = stackIndex >= config.maxStackDepth;
      const finalX = rightEdge - tabW + stackIndex * config.rightStackStep;
      positions[i] = {
        x: finalX,
        isStacked: true,
        isLeftStacked: false,
        stackIndex,
        isHidden,
      };
    } else if (leftStackedIndices.includes(i)) {
      const lIdx = leftStackedIndices.indexOf(i);
      const reverseRank = leftStackedIndices.length - 1 - lIdx;
      const stackIndex = reverseRank;
      const isHidden = stackIndex >= config.maxStackDepth;
      // When left-stacked, add 20px space for stacked card breathing room
      const finalX = leftEdge + leftStackedPad - stackIndex * config.leftStackStep;
      positions[i] = {
        x: finalX,
        isStacked: true,
        isLeftStacked: true,
        stackIndex,
        isHidden,
      };
    } else {
      positions[i] = {
        x: naturalX,
        isStacked: false,
        isLeftStacked: false,
        stackIndex: 0,
        isHidden: false,
      };
    }
  }

  // Draw order: Left-stacked tabs drawn left-to-right (earlier tabs below later tabs),
  // Right-stacked & unstacked in descending order, Active tab ALWAYS drawn last (on top)
  const drawOrder: number[] = [];

  for (const idx of leftStackedIndices) {
    if (idx !== activeIndex) drawOrder.push(idx);
  }

  for (let i = rightStackedIndices.length - 1; i >= 0; i--) {
    const idx = rightStackedIndices[i];
    if (idx !== activeIndex) drawOrder.push(idx);
  }

  for (let i = tabsCount - 1; i >= 0; i--) {
    if (!leftStackedIndices.includes(i) && !rightStackedIndices.includes(i) && i !== activeIndex) {
      drawOrder.push(i);
    }
  }

  if (activeIndex >= 0 && activeIndex < tabsCount) {
    drawOrder.push(activeIndex);
  }

  return {
    containerRef,
    scrollRef,
    containerWidth,
    tabW,
    tabStep,
    totalW,
    overflow,
    positions,
    drawOrder,
    scrollToActiveTab,
    config,
  };
}
